// AddActionMenu.jsx

import React, { useMemo } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Components
import ContextMenu from 'components/contextmenu/ContextMenu';
import {
  buildKbAddMenuChildren,
  buildNodeAddMenuChildren,
} from 'components/addmenu/buildAddMenuChildren';

/**
 * AddActionMenu
 *
 * Sectioned context menu for Knowledge Base More or node Link / Add.
 *
 * @param {'kb'|'node'} mode - Menu variant.
 * @param {object} triggerRef - Ref attached to the trigger button.
 * @param {function} onTypeSelect - Called with a type or media action key.
 * @param {string[]|null} [relatedTypeKeys] - Node mode: host suggestedChildren allowlist.
 * @param {function} [onLinkExisting] - Node mode: open link-existing search.
 * @param {function} [onImportTypeSelect] - Node mode: open CSV import for a type.
 * @param {boolean} [showPurchaseOrderItemsImport] - Node mode: show denormalized PO-item import.
 * @param {function} [onImportPurchaseOrderItems] - Node mode: open PO-item import modal.
 *
 * @returns {ReactNode}
 */
const AddActionMenu = React.memo(({
  mode = 'kb',
  triggerRef,
  onTypeSelect,
  relatedTypeKeys = null,
  onLinkExisting,
  onImportTypeSelect,
  showPurchaseOrderItemsImport = false,
  onImportPurchaseOrderItems,
}) => {

  // ----- CONTEXT ----- //

  const { getAll } = useNodeTypes();

  const typeDefs = useMemo(() => getAll() || [], [getAll]);

  const menuChildren = useMemo(() => {
    if (mode === 'node') {
      return buildNodeAddMenuChildren({
        typeDefs,
        relatedTypeKeys,
        onLinkExisting,
        onImportTypeSelect,
        showPurchaseOrderItemsImport,
        onImportPurchaseOrderItems,
        onTypeSelect,
      });
    }
    return buildKbAddMenuChildren({
      onTypeSelect,
      typeDefs,
    });
  }, [
    mode,
    onImportPurchaseOrderItems,
    onImportTypeSelect,
    onLinkExisting,
    onTypeSelect,
    relatedTypeKeys,
    showPurchaseOrderItemsImport,
    typeDefs,
  ]);

  // ----- RENDER ----- //

  return (
    <ContextMenu
      triggerMode="click"
      triggerRef={triggerRef}
    >
      {menuChildren}
    </ContextMenu>
  );
});

// Dev Tools
AddActionMenu.displayName = 'AddActionMenu';

export default AddActionMenu;
